import React from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, EyeOff, Book } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTemporaryChatStore } from "../store/temporary-chat-store";

export const TemporaryChatModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    didShowInfoModal,
    setDidShowInfoModal,
    isTemporaryMode,
    disableTemporaryMode,
  } = useTemporaryChatStore();

  const handleConfirm = () => {
    setDidShowInfoModal(true);
  };

  const handleCancel = () => {
    disableTemporaryMode();
    setDidShowInfoModal(false);
  };

  if (didShowInfoModal || !isTemporaryMode) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
      {/* Overlay Content */}
      <div className="max-w-[477px] w-full mx-4 bg-white dark:bg-gray-800 rounded-[20px] p-8 shadow-2xl">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {t("temporaryChat.confirmation.title")}
          </h2>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4">
          {/* Feature Cards Container */}
          <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col gap-6">
            {/* Feature 1: Not in history */}
            <div className="flex gap-3">
              <MessageCircle className="w-6 h-6 text-gray-900 dark:text-gray-100 shrink-0" />
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t("temporaryChat.modal.notInHistory.title")}
                </h3>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-300 leading-5">
                  {t("temporaryChat.modal.notInHistory.description")}
                </p>
              </div>
            </div>

            {/* Feature 2: No model training */}
            <div className="flex gap-3">
              <EyeOff className="w-6 h-6 text-gray-900 dark:text-gray-100 shrink-0" />
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t("temporaryChat.modal.noModelTraining.title")}
                </h3>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-300 leading-5">
                  {t("temporaryChat.modal.noModelTraining.description")}
                </p>
              </div>
            </div>

            {/* Feature 3: Memory off */}
            <div className="flex gap-3">
              <Book className="w-6 h-6 text-gray-900 dark:text-gray-100 shrink-0" />
              <div className="flex flex-col gap-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t("temporaryChat.modal.memoryOff.title")}
                </h3>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-300 leading-5">
                  {t("temporaryChat.modal.memoryOff.description")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Cancel and Continue Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="px-4 py-2.5 text-sm font-semibold border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="darkPrimary"
            onClick={handleConfirm}
            className="text-white dark:text-gray-100 hover:bg-brand-600/90 dark:hover:bg-gray-600 rounded-lg px-4 py-2.5 text-sm font-semibold"
          >
            {t("checkout.continue")}
          </Button>
        </div>
      </div>
    </div>
  );
};
